<script lang="ts">
import Button1XP from '../Button1XP/Button1XP.vue';
import FormSelect from '../FormSelect/FormSelect.vue';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
</script>

<script setup lang="ts">
const emit = defineEmits(['update']);

const props = defineProps({
  results: {
    type: Number,
    default: null,
  },
  config: {
    type: Object,
    default: () => {},
  },
});
const pages = ref<number | null>(null);
const currentPage = ref(1);
const currentPageNumber = ref<string | null>(null);
const resultsPerPage = ref(props.config.resultsPerPage);
const initialPagesSelectOption = ref('1');
const pagesSelectOptions = ref<string[]>([]);
const resultsPerPageSelectOptions = ref<unknown[]>([]);
const displayRange = ref<[number, number]>([0, props.config.resultsPerPage - 1]);
const initialSelectOption = ref([]);

const updatePages = () => {
  currentPage.value = 1;
  initialPagesSelectOption.value = currentPage.value.toString();
  const updatedPageCount = Math.ceil(props.results / resultsPerPage.value);
  pages.value = updatedPageCount;
  pagesSelectOptions.value = [];
  for (let i = 1; i < pages.value + 1; i++) {
    pagesSelectOptions.value.push(i.toString());
  }

  setDisplayRange();
};

const setDisplayRange = () => {
  displayRange.value = [
    currentPage.value * resultsPerPage.value - resultsPerPage.value,
    currentPage.value * resultsPerPage.value - 1,
  ];
  emit('update', displayRange.value);
};

const setDisplayRangeFromPageChange = () => {
  displayRange.value = [
    currentPage.value * resultsPerPage.value - resultsPerPage.value,
    currentPage.value * resultsPerPage.value - 1,
  ];
  emit('update', displayRange.value);
};

const updateResults = (results) => {
  const index = resultsPerPageSelectOptions.value
    .map((resultsPerPage) => resultsPerPage)
    .indexOf(results[0]);
  if (index === 0) {
    resultsPerPage.value = props.config.resultsPerPage;
  } else if (index === 1) {
    resultsPerPage.value = props.config.resultsPerPage * 2;
  } else if (index === 2) {
    resultsPerPage.value = 999999;
  }

  updatePages();
};

const updateCurrentPage = (page) => {
  currentPage.value = +page;
  initialPagesSelectOption.value = currentPage.value.toString();
  setDisplayRangeFromPageChange();
};

watch(
  () => props.results,
  (newResults, oldResults) => {
    if (newResults !== oldResults) {
      updatePages();
    }
  },
);

watch(currentPage, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    currentPageNumber.value = newValue.toString();
  }
});

onBeforeMount(() => {
  initialSelectOption.value = props.config.resultsSelect.initialSelectOption;
});

onMounted(() => {
  if (typeof props.config.resultsSelect.selectOptions === 'object') {
    props.config.resultsSelect.selectOptions.forEach((selectOption) => {
      resultsPerPageSelectOptions.value.push(selectOption);
    });
  } else {
    resultsPerPageSelectOptions.value = props.config.resultsSelect.selectOptions;
  }

  updatePages();
});

const isPreviousDisabled = computed(() => currentPage.value === 1);
const isNextDisabled = computed(() => props.results === 0 || currentPage.value === pages.value);
</script>

<template>
  <form :class="$style.container">
    <FormSelect
      v-model="initialSelectOption"
      :class="$style.upper"
      colour="oneXP"
      :disabled="props.results === 0 ? true : false"
      :has-filter="false"
      :initial-option="initialSelectOption"
      :options="resultsPerPageSelectOptions"
      variant="small"
      @update="updateResults($event)"
    />
    <div :class="$style.lower">
      <div :class="$style.pages">
        <span :class="$style.text">{{ config.texts.page }}</span>
        <FormSelect
          v-model="currentPageNumber"
          colour="oneXP"
          :disabled="props.results === 0 || pages === 1"
          :external-value="currentPageNumber ?? undefined"
          :has-filter="false"
          :initial-option="'1'"
          :options="pagesSelectOptions"
          variant="small"
          @update="updateCurrentPage"
        />
        <span :class="$style.text">{{ config.texts.of }} {{ pages }}</span>
      </div>
      <div :class="$style.navigation">
        <Button1XP
          aria-label="previous"
          :class="$style.previous"
          :is-disabled="isPreviousDisabled"
          size="xs"
          start-icon="chevron_left"
          variant="ghost"
          @click="currentPage--"
        />
        <Button1XP
          aria-label="next"
          :class="$style.next"
          :is-disabled="isNextDisabled"
          size="xs"
          start-icon="chevron_right"
          variant="ghost"
          @click="currentPage++"
        />
      </div>
    </div>
  </form>
</template>

<style module lang="scss" src="./Pagination.scss" />
